/**
 * Installation detail page
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getInstallation, updateInstallation, deleteInstallation } from '../services/installations';
import { listChecklistRuns } from '../services/checklists';
import { listEvidences } from '../services/evidences';
import { listMeasurements } from '../services/measurements';
import { listFindings } from '../services/findings';
import { useAuth } from '../contexts/AuthContext';
import type { Installation, ChecklistRun, Evidence, Measurement, ComplianceFinding } from '../../../shared/src/types';
import '../styles/InstallationDetail.css';

export default function InstallationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [installation, setInstallation] = useState<Installation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [checklistRuns, setChecklistRuns] = useState<ChecklistRun[]>([]);
  const [evidences, setEvidences] = useState<Evidence[]>([]);
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [findings, setFindings] = useState<ComplianceFinding[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'checklists' | 'evidences' | 'measurements' | 'findings'>('overview');

  const canEdit = user?.role === 'Admin' || user?.role === 'Supervisor';
  const canDelete = user?.role === 'Admin';

  useEffect(() => {
    if (id) {
      loadInstallation();
    }
  }, [id]);

  const loadInstallation = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError('');
      const [inst, runs, meas, finds] = await Promise.all([
        getInstallation(id),
        listChecklistRuns(id),
        listMeasurements(id),
        listFindings(id),
      ]);
      setInstallation(inst);
      setChecklistRuns(runs);
      
      // Get evidences from all checklist runs of this installation
      const allEvidences: Evidence[] = [];
      for (const run of runs) {
        try {
          const runEvidences = await listEvidences(run.id);
          allEvidences.push(...runEvidences);
        } catch (err) {
          // Continue if error
        }
      }
      setEvidences(allEvidences);
      setMeasurements(meas);
      setFindings(finds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading installation');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!id || !installation) return;

    const formData = new FormData(e.currentTarget);

    try {
      setSaving(true);
      setError('');

      const data = {
        name: formData.get('name') as string,
        type: formData.get('type') as 'residential' | 'industrial' | 'commercial',
        address: formData.get('address') as string || undefined,
        status: formData.get('status') as 'active' | 'completed' | 'archived',
      };

      const updated = await updateInstallation(id, data);
      setInstallation(updated);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error updating installation');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    if (!confirm('¿Estás seguro de que quieres eliminar esta instalación? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      setError('');
      await deleteInstallation(id);
      navigate('/installations');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error deleting installation');
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'residential':
        return 'Residencial';
      case 'industrial':
        return 'Industrial';
      case 'commercial':
        return 'Comercial';
      default:
        return type;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Activa';
      case 'completed':
        return 'Completada';
      case 'archived':
        return 'Archivada';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="installation-detail">
        <div className="loading">Cargando instalación...</div>
      </div>
    );
  }

  if (!installation) {
    return (
      <div className="installation-detail">
        <div className="error-message">Instalación no encontrada</div>
        <button className="btn-secondary" onClick={() => navigate('/installations')}>
          Volver a instalaciones
        </button>
      </div>
    );
  }

  return (
    <div className="installation-detail">
      <div className="detail-header">
        <button className="btn-back" onClick={() => navigate('/installations')}>
          ← Volver
        </button>
        <div className="header-actions">
          {canEdit && !editing && (
            <button className="btn-secondary" onClick={() => setEditing(true)}>
              Editar
            </button>
          )}
          {canDelete && !editing && (
            <button className="btn-destructive" onClick={handleDelete}>
              Eliminar
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {editing ? (
        <div className="edit-form-card">
          <h2>Editar instalación</h2>
          <form onSubmit={handleUpdate}>
            <div className="form-field">
              <label htmlFor="name">Nombre *</label>
              <input
                id="name"
                name="name"
                type="text"
                defaultValue={installation.name}
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="type">Tipo *</label>
              <select id="type" name="type" defaultValue={installation.type} required>
                <option value="residential">Residencial</option>
                <option value="industrial">Industrial</option>
                <option value="commercial">Comercial</option>
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="address">Dirección</label>
              <input
                id="address"
                name="address"
                type="text"
                defaultValue={installation.address || ''}
              />
            </div>

            {canEdit && (
              <div className="form-field">
                <label htmlFor="status">Estado *</label>
                <select id="status" name="status" defaultValue={installation.status} required>
                  <option value="active">Activa</option>
                  <option value="completed">Completada</option>
                  <option value="archived">Archivada</option>
                </select>
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar cambios'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setEditing(false)}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="detail-card">
          <div className="detail-title">
            <h1>{installation.name}</h1>
            <span className={`status-badge status-${installation.status}`}>
              {getStatusLabel(installation.status)}
            </span>
          </div>

          <div className="detail-info">
            <div className="info-row">
              <span className="info-label">Tipo:</span>
              <span className="info-value">{getTypeLabel(installation.type)}</span>
            </div>

            {installation.address && (
              <div className="info-row">
                <span className="info-label">Dirección:</span>
                <span className="info-value">{installation.address}</span>
              </div>
            )}

            <div className="info-row">
              <span className="info-label">Estado:</span>
              <span className="info-value">{getStatusLabel(installation.status)}</span>
            </div>

            <div className="info-row">
              <span className="info-label">Creada:</span>
              <span className="info-value">
                {new Date(installation.created_at).toLocaleString('es-ES')}
              </span>
            </div>

            <div className="info-row">
              <span className="info-label">Última actualización:</span>
              <span className="info-value">
                {new Date(installation.updated_at).toLocaleString('es-ES')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Tabs for related data */}
      {!editing && (
        <div className="installation-tabs">
          <button
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => setActiveTab('overview')}
          >
            Resumen
          </button>
          <button
            className={activeTab === 'checklists' ? 'active' : ''}
            onClick={() => setActiveTab('checklists')}
          >
            Checklists ({checklistRuns.length})
          </button>
          <button
            className={activeTab === 'evidences' ? 'active' : ''}
            onClick={() => setActiveTab('evidences')}
          >
            Evidencias ({evidences.length})
          </button>
          <button
            className={activeTab === 'measurements' ? 'active' : ''}
            onClick={() => setActiveTab('measurements')}
          >
            Mediciones ({measurements.length})
          </button>
          <button
            className={activeTab === 'findings' ? 'active' : ''}
            onClick={() => setActiveTab('findings')}
          >
            Hallazgos ({findings.length})
          </button>
        </div>
      )}

      {/* Tab Content */}
      {!editing && (
        <div className="tab-content-container">
          {activeTab === 'checklists' && (
            <div className="checklists-section">
              <div className="section-header">
                <h2>Checklists de esta instalación</h2>
                {canEdit && (
                  <button
                    className="btn-primary"
                    onClick={() => navigate('/checklists', { state: { installationId: id } })}
                  >
                    Nuevo checklist
                  </button>
                )}
              </div>
              {checklistRuns.length === 0 ? (
                <p className="empty-state">No hay checklists para esta instalación</p>
              ) : (
                <div className="checklists-grid">
                  {checklistRuns.map((run) => (
                    <div key={run.id} className="checklist-card" onClick={() => navigate(`/checklists/${run.id}`)}>
                      <div className="card-header">
                        <h3>Checklist #{run.id.substring(0, 8)}</h3>
                        <span className={`status-badge status-${run.status}`}>
                          {run.status === 'pending' ? 'Pendiente' : 
                           run.status === 'in_progress' ? 'En progreso' :
                           run.status === 'completed' ? 'Completado' :
                           run.status === 'closed' ? 'Cerrado' : run.status}
                        </span>
                      </div>
                      <div className="card-body">
                        <div className="info-row">
                          <span className="info-label">Creado:</span>
                          <span className="info-value">
                            {new Date(run.created_at).toLocaleDateString('es-ES')}
                          </span>
                        </div>
                      </div>
                      <div className="card-actions">
                        <button className="btn-secondary" onClick={(e) => { e.stopPropagation(); navigate(`/checklists/${run.id}`); }}>
                          Ver detalle
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'evidences' && (
            <div className="evidences-section">
              <h2>Evidencias de esta instalación</h2>
              {evidences.length === 0 ? (
                <p className="empty-state">No hay evidencias para esta instalación</p>
              ) : (
                <div className="evidences-list">
                  {evidences.map((evidence) => (
                    <div key={evidence.id} className="evidence-card">
                      <div className="evidence-info">
                        <strong>{evidence.filename}</strong>
                        <span className="evidence-type">{evidence.type}</span>
                      </div>
                      <div className="evidence-meta">
                        <span>{new Date(evidence.created_at).toLocaleDateString('es-ES')}</span>
                        <button
                          className="btn-secondary btn-sm"
                          onClick={() => navigate(`/checklists/${evidence.checklist_run_id}`)}
                        >
                          Ver checklist
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'measurements' && (
            <div className="measurements-section">
              <h2>Mediciones de esta instalación</h2>
              {measurements.length === 0 ? (
                <p className="empty-state">No hay mediciones para esta instalación</p>
              ) : (
                <div className="measurements-list">
                  {measurements.map((measurement) => (
                    <div key={measurement.id} className="measurement-card">
                      <div className="measurement-header">
                        <strong>{measurement.type}</strong>
                        <span className="measurement-value">{measurement.value} {measurement.unit}</span>
                      </div>
                      {measurement.notes && (
                        <p className="measurement-notes">{measurement.notes}</p>
                      )}
                      <div className="measurement-meta">
                        <span>{new Date(measurement.created_at).toLocaleDateString('es-ES')}</span>
                        {measurement.checklist_item_run_id && (
                          <button
                            className="btn-secondary btn-sm"
                            onClick={() => {
                              // We need to find which checklist this item belongs to
                              // For now, navigate to checklists page
                              navigate('/checklists');
                            }}
                          >
                            Ver item
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'findings' && (
            <div className="findings-section">
              <h2>Hallazgos de esta instalación</h2>
              {findings.length === 0 ? (
                <p className="empty-state">No hay hallazgos para esta instalación</p>
              ) : (
                <div className="findings-list">
                  {findings.map((finding) => (
                    <div key={finding.id} className={`finding-card finding-${finding.severity}`}>
                      <div className="finding-header">
                        <strong>Severidad: {finding.severity}</strong>
                        <span className={`status-badge status-${finding.status}`}>
                          {finding.status === 'open' ? 'Abierto' :
                           finding.status === 'resolved' ? 'Resuelto' :
                           finding.status === 'dismissed' ? 'Descartado' : finding.status}
                        </span>
                      </div>
                      <p className="finding-description">{finding.description}</p>
                      {finding.recommendation && (
                        <p className="finding-recommendation">{finding.recommendation}</p>
                      )}
                      <div className="finding-meta">
                        <span>{new Date(finding.created_at).toLocaleDateString('es-ES')}</span>
                        {finding.checklist_run_id && (
                          <button
                            className="btn-secondary btn-sm"
                            onClick={() => navigate(`/checklists/${finding.checklist_run_id}`)}
                          >
                            Ver checklist
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'overview' && (
            <div className="overview-section">
              <h2>Resumen</h2>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-label">Checklists</div>
                  <div className="stat-value">{checklistRuns.length}</div>
                  <button className="btn-link" onClick={() => setActiveTab('checklists')}>
                    Ver todos
                  </button>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Evidencias</div>
                  <div className="stat-value">{evidences.length}</div>
                  <button className="btn-link" onClick={() => setActiveTab('evidences')}>
                    Ver todas
                  </button>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Mediciones</div>
                  <div className="stat-value">{measurements.length}</div>
                  <button className="btn-link" onClick={() => setActiveTab('measurements')}>
                    Ver todas
                  </button>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Hallazgos</div>
                  <div className="stat-value">{findings.filter(f => f.status === 'open').length} abiertos</div>
                  <button className="btn-link" onClick={() => setActiveTab('findings')}>
                    Ver todos
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

